<template>
  <div class="w-50 mt-12 ml-16">
    <SoErrorBoundary>
      <div v-if="userQuery.isFetching">
        <SoSkeleton height="250px" class="my-3" />
        <SoSkeleton height="60px" width="200px" class="mt-5" />
      </div>
      <SoAlert v-else-if="userQuery.isError" type="error">
        Failed to load user
      </SoAlert>
      <template v-else-if="userQuery.data">
        <SoForm
          data-testid="user-edit-form"
          :disabled="!!userUpdateMutation.isPending"
          @submit="handleAction()"
        >
          <label
            class="user-edit__inline-input"
            data-testid="user-edit-field-email"
          >
            Email
            <SoTextField
              v-model="form.email"
              :rules="[rules.email, rules.required]"
              type="text"
              name="user-email"
            />
          </label>
          <label class="user-edit__inline-input">
            Password
            <SoTextField
              v-model="form.password"
              data-testid="user-edit-field-password"
              type="password"
            />
          </label>
          <label class="user-edit__inline-input">
            Password confirmation
            <SoTextField
              v-model="form.passwordConfirmation"
              data-testid="user-edit-field-password-confirmation"
              type="password"
            />
          </label>
          <label class="user-edit__inline-input checkbox">
            Lock account
            <SoCheckbox
              ref="junifer-filter-checkbox"
              v-model="form.isLocked"
              data-testid="user-edit-field-locked"
              :label="form.isLocked ? `Locked` : `Unlocked`"
            />
          </label>
          <template #actions>
            <div class="user-edit__actions mt-8 gap-2">
              <SoButton
                v-if="userQuery.data?.id"
                title="Get OTP"
                large
                data-testid="user-get-otp"
                :loading="userQuery.isFetching"
                :disabled="!userQuery.data"
                @click="copyUserOtp(userQuery.data.id)"
              />
              <SoButton
                title="save"
                type="success"
                submit
                large
                :loading="
                  !!userUpdateMutation.isPending ||
                  !!updatePasswordMutation.isPending
                "
                data-testid="user-save-btn"
              />
              <slot name="form-action" />
            </div>
          </template>
        </SoForm>
        <slot name="referral-code" />
        <slot name="cancellations" />
        <slot name="renewals" />
      </template>
      <SoAlert v-else type="error">User does not exist</SoAlert>
    </SoErrorBoundary>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { UserService } from "@/services/admin/UserService"
  import SoErrorBoundary from "@/components/SoErrorBoundary.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import rules from "@/validators/rules"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import { useSnackbarStore } from "@/stores/snackbar"
  import { errorReporter } from "@/services/ErrorService"
  import { IUser } from "@/models/admin/user/user.types"
  import { UserMutator } from "@/mutators/user/UserMutator"
  import dayjs from "dayjs"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoAlert from "@/components/SoAlert.vue"

  export default defineComponent({
    components: {
      SoCheckbox,
      SoButton,
      SoForm,
      SoTextField,
      SoErrorBoundary,
      SoSkeleton,
      SoAlert,
    },
    data() {
      return {
        userId: Number(this.$route.params.userId),
        snackbar: useSnackbarStore(),
        userQuery: UserService.getUser(Number(this.$route.params.userId)),
        userUpdateMutation: UserMutator.update(),
        updatePasswordMutation: UserMutator.updatePassword(),
        rules: rules,
        form: {
          email: "",
          password: "",
          passwordConfirmation: "",
          isLocked: false,
        },
      }
    },
    watch: {
      "userQuery.data": {
        handler(user: IUser) {
          if (!user) return
          this.form.email = user.email
          this.form.isLocked = !!user.lockedAt
        },
        deep: true,
        immediate: true,
      },
    },
    methods: {
      async handleAction() {
        if (this.form.password !== this.form.passwordConfirmation) {
          return this.snackbar.addError(
            "Password and password confirmation do not match",
          )
        }

        try {
          if (
            this.form.email !== this.userQuery.data?.email ||
            this.form.isLocked !== !!this.userQuery.data?.lockedAt
          ) {
            await this.userUpdateMutation.mutate({
              userId: this.userId,
              email: this.form.email,
              lockedAt: this.form.isLocked ? dayjs() : undefined,
            })

            this.snackbar.addSuccess("User information updated")
          }
          if (this.form.password && this.form.passwordConfirmation) {
            await this.updatePasswordMutation.mutate({
              userId: this.userId,
              password: this.form.password,
              passwordConfirmation: this.form.passwordConfirmation,
            })
            this.snackbar.addSuccess("User password updated")
          }
        } catch (error) {
          this.snackbar.addError("Failed to update user information")
          throw error
        }
      },

      async copyUserOtp(userId: number) {
        try {
          await UserService.getOtp(userId, true)
          this.snackbar.addSuccess("OTP copied to clipboard")
        } catch (e) {
          errorReporter.report(e)
          console.error(e)
          this.snackbar.addError(
            "Sorry, we couldn't retrieve the OTP. Please try again.",
          )
        }
      },
    },
  })
</script>

<style scoped>
  .user-edit__inline-input {
    margin-top: 24px;
    display: block;
  }

  .user-edit__inline-input.checkbox {
    display: inline-block;
  }

  .user-edit__actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
</style>
